import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2';
import axios from 'axios';

interface DirectoryItem {
  name: string;
  type: 'directory' | 'file';
  path: string;
  fullPath: string;
  size: number;
  created: string;
  modified: string;
  children?: DirectoryItem[];
  childrenCount?: number;
}

interface ApiResponse {
  message: string;
  data: DirectoryItem[];
  totals: {
    files: number;
    directories: number;
    total: number;
  };
  path: string;
}

const Modules = () => {
  document.title = 'Source Structure | CITY SERVICE';

  const [isLoading, setIsLoading] = useState(false);
  const [directoryData, setDirectoryData] = useState<DirectoryItem[]>([]);
  const [responseInfo, setResponseInfo] = useState<{ totals: { files: number; directories: number; total: number }; path: string } | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const toggleExpanded = (path: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedItems(newExpanded);
  };

  const filterDirectoryData = (items: DirectoryItem[], searchTerm: string): DirectoryItem[] => {
    if (!searchTerm.trim()) return items;

    return items.reduce((filtered: DirectoryItem[], item) => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      let filteredChildren: DirectoryItem[] = [];
      
      if (item.children && item.children.length > 0) {
        filteredChildren = filterDirectoryData(item.children, searchTerm);
      }

      // Include item if it matches search or has matching children
      if (matchesSearch || filteredChildren.length > 0) {
        filtered.push({
          ...item,
          children: filteredChildren
        });
      }

      return filtered;
    }, []);
  };



  const fetchModules = async () => {
    setIsLoading(true);
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the directory structure.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/cli/src-structure', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const apiResponse: ApiResponse = response.data;
      setDirectoryData(apiResponse.data || []);
      setResponseInfo({
        totals: apiResponse.totals,
        path: apiResponse.path
      });

      Swal.fire({
        icon: 'success',
        title: 'Directory Structure Loaded',
        showConfirmButton: false,
        timer: 1000,
      });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to fetch directory structure',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };



  const renderTreeItem = (item: DirectoryItem, level: number = 0, isLast: boolean = false, parentPrefix: string = ""): React.ReactNode => {
    const isExpanded = expandedItems.has(item.path);
    const hasChildren = item.children && item.children.length > 0;
    
    // Create tree line prefix
    const currentPrefix = level === 0 ? "" : parentPrefix + (isLast ? "└── " : "├── ");
    const childPrefix = level === 0 ? "" : parentPrefix + (isLast ? "    " : "│   ");

    return (
      <div key={item.path} className="tree-item">
        <div className="tree-line d-flex align-items-center py-1 px-2 hover-bg-light position-relative">
          {/* Tree structure lines */}
          {level > 0 && (
            <span 
              className="tree-prefix text-muted me-2" 
              style={{ 
                fontFamily: 'monospace', 
                fontSize: '14px',
                whiteSpace: 'pre',
                lineHeight: '1.2'
              }}
            >
              {currentPrefix}
            </span>
          )}
          
          {/* Expand/Collapse button for directories */}
          {hasChildren && (
            <button
              className="btn btn-sm btn-link p-0 me-2 text-decoration-none"
              onClick={() => toggleExpanded(item.path)}
              style={{ 
                width: '16px', 
                height: '16px',
                fontSize: '10px',
                border: '1px solid #dee2e6',
                borderRadius: '2px',
                background: '#fff'
              }}
            >
              {isExpanded ? '−' : '+'}
            </button>
          )}
          
          {/* Folder/File icon */}
          <div className="d-flex align-items-center me-2">
            {item.type === 'directory' && (
              <i className={`${isExpanded ? 'ri-folder-open-fill' : 'ri-folder-fill'} text-warning`} style={{ fontSize: '16px' }}></i>
            )}
          </div>
          
          {/* File/Directory name and info */}
          <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <span className={`${item.type === 'directory' ? 'fw-semibold text-dark' : 'fw-normal text-secondary'}`}>
                    {item.name}
                  </span>
                  {item.type === 'directory' && item.childrenCount && (
                    <small className="text-muted ms-2 badge bg-light text-dark">
                      {item.childrenCount} items
                    </small>
                  )}
                  {item.type === 'file' && (
                    <small className="text-muted ms-2">
                      {(item.size / 1024).toFixed(1)} KB
                    </small>
                  )}
                </div>
                
                <div className="d-flex align-items-center">
                  <small className="text-muted" style={{ fontSize: '11px' }}>
                    {new Date(item.created).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: '2-digit'
                    })}
                  </small>
                </div>
              </div>
          </div>
        </div>
        
        {/* Children */}
        {isExpanded && hasChildren && (
          <div className="tree-children">
            {item.children?.map((child, index) => 
              renderTreeItem(
                child, 
                level + 1, 
                index === (item.children?.length || 0) - 1,
                childPrefix
              )
            )}
          </div>
        )}
      </div>
    );
  };



  useEffect(() => {
    fetchModules();
  }, []);

  // Auto-expand items when searching
  useEffect(() => {
    if (searchTerm.trim()) {
      const expandPaths = (items: DirectoryItem[]) => {
        const paths = new Set<string>();
        const traverse = (item: DirectoryItem) => {
          if (item.children && item.children.length > 0) {
            paths.add(item.path);
            item.children.forEach(traverse);
          }
        };
        items.forEach(traverse);
        return paths;
      };
      
      const filteredData = filterDirectoryData(directoryData, searchTerm);
      const pathsToExpand = expandPaths(filteredData);
      setExpandedItems(pathsToExpand);
    }
  }, [searchTerm, directoryData]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Source Structure" title="Development" />

          {responseInfo && (
            <Row className="mb-3">
              <Col lg={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="py-2">
                    <div className="d-flex justify-content-between align-items-center text-muted small">
                      <span><strong>Files:</strong> {responseInfo.totals.files}</span>
                      <span><strong>Directories:</strong> {responseInfo.totals.directories}</span>
                      <span><strong>Total:</strong> {responseInfo.totals.total} items</span>
                      <span><strong>Path:</strong> {responseInfo.path}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Directory Structure</h5>
                    <div className="ms-auto" style={{ minWidth: '300px' }}>
                      <div className="position-relative">
                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"></i>
                        <input
                          type="text"
                          className="form-control ps-4"
                          placeholder="Search files and directories..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{
                            fontSize: '14px',
                            borderRadius: '6px',
                            border: '1px solid #e9ecef'
                          }}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-1"
                            onClick={() => setSearchTerm('')}
                            style={{ 
                              border: 'none',
                              background: 'transparent',
                              padding: '2px 6px'
                            }}
                          >
                            <i className="ri-close-line text-muted"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="tree-view" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {(() => {
                      const filteredData = filterDirectoryData(directoryData, searchTerm);
                      return Array.isArray(filteredData) && filteredData.length > 0 ? (
                        filteredData.map((item, index) => 
                          renderTreeItem(item, 0, index === filteredData.length - 1, "")
                        )
                      ) : searchTerm ? (
                        <div className="text-center p-4 text-muted">
                          <i className="ri-search-line mb-2" style={{ fontSize: '24px' }}></i>
                          <div>No results found for "{searchTerm}"</div>
                          <small>Try a different search term</small>
                        </div>
                      ) : (
                        <div className="text-center p-4 text-muted">
                          No directory structure available
                        </div>
                      );
                    })()}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        <style>{`
          .hover-bg-light:hover {
            background-color: #f8f9fa !important;
          }
          
          .tree-item {
            user-select: none;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          }
          
          .tree-view {
            border: 1px solid #e9ecef;
            border-radius: 0.5rem;
            background-color: #ffffff;
            font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
          }
          
          .tree-line {
            border-bottom: 1px solid transparent;
            transition: background-color 0.15s ease-in-out;
            min-height: 32px;
            position: relative;
          }
          
          .tree-line:hover {
            background-color: #f8f9fa !important;
            border-bottom-color: #e9ecef;
          }
          
          .tree-prefix {
            color: #6c757d;
            font-weight: 400;
            font-size: 14px;
          }
          
          .tree-children {
            position: relative;
          }
          
          /* File type colors */
          .ri-braces-line { color: #007acc !important; } /* TypeScript */
          .ri-javascript-line { color: #f7df1e !important; background: #323330; border-radius: 2px; } /* JavaScript */
          .ri-reactjs-line { color: #61dafb !important; } /* React */
          .ri-css3-line { color: #1572b6 !important; } /* CSS */
          .ri-html5-line { color: #e34f26 !important; } /* HTML */
          .ri-markdown-line { color: #083fa1 !important; } /* Markdown */
          .ri-test-tube-line { color: #10b981 !important; } /* Test files */
          .ri-code-s-slash-line { color: #fb8500 !important; } /* JSON */
          
          /* Folder icons */
          .ri-folder-fill { 
            color: #fbbf24 !important;
            filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
          }
          .ri-folder-open-fill { 
            color: #f59e0b !important;
            filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
          }
          
          /* Badge styling */
          .badge {
            font-size: 0.65em;
            font-weight: 500;
          }
          
          /* Scrollbar styling */
          .tree-view::-webkit-scrollbar {
            width: 8px;
          }
          
          .tree-view::-webkit-scrollbar-track {
            background: #f1f3f4;
            border-radius: 4px;
          }
          
          .tree-view::-webkit-scrollbar-thumb {
            background: #c1c8cd;
            border-radius: 4px;
          }
          
          .tree-view::-webkit-scrollbar-thumb:hover {
            background: #a8b3ba;
          }
          
          /* Enhanced visual hierarchy */
          .tree-line {
            padding: 4px 8px;
            margin: 1px 0;
          }
          
          .tree-line:first-child {
            border-top-left-radius: 0.5rem;
            border-top-right-radius: 0.5rem;
          }
          
          .tree-line:last-child {
            border-bottom-left-radius: 0.5rem;
            border-bottom-right-radius: 0.5rem;
          }
        `}</style>
      </div>


    </React.Fragment>
  );
};

export default Modules;
